import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Map, List } from 'immutable';

import useAddToCart from './useAddToCart';
import { isDigitalProduct } from '../containers/Cart/Checkout/helpers/digitalProductHelpers';
import { type DiscountTier } from '../containers/Cart/Checkout/helpers/useCartProduct';

/**
 * Configuration for product entity structure
 */
export interface ProductEntityConfig {
  /**
   * Path to product entity from paragraph entity.
   * - Paragraph products: ['field_product', 0, 'entity']
   * - Generic products: [] (empty - variations at root)
   */
  entityPath?: (string | number)[];
  /**
   * Minimum quantity allowed
   * @default 1
   */
  minQuantity?: number;
  /**
   * Maximum quantity allowed
   * @default 10
   */
  maxQuantity?: number;
  /**
   * Whether to track view_item analytics
   * @default true
   */
  trackViewItem?: boolean;
}

/**
 * Return type for useProduct hook
 */
interface UseProductReturn {
  /** Current quantity value */
  quantity: number;
  /** Update quantity */
  setQuantity: (value: number) => void;
  /** Current variation index */
  variationIndex: number;
  /** Update variation index */
  setVariationIndex: (index: number) => void;
  /** Handler for add to cart button click */
  handleOrderClick: () => Promise<void>;
  /** Handler for variation select change */
  handleVariationChange: (index: number | null) => void;
  /** Whether add to cart is in progress */
  isLoading: boolean;
  /** Product title */
  title: string;
  /** Current variation price */
  variationPrice: number;
  /** Product SKU */
  sku: string;
  /** Whether product has multiple variations */
  hasMultipleVariations: boolean;
  /** Product body/description HTML */
  body: string;
  /** Whether product is a digital product (e-learning or digital tool) */
  isDigitalProduct: boolean;
  /** Whether product is available for purchase */
  isAvailable: boolean;
  /** Volume discount tiers for the current variation (empty when none apply) */
  discountTiers: DiscountTier[];
  /** Resolved product image URL, or null when the product has no image */
  productImage: string | null;
}

/**
 * Hook for managing product display and ordering functionality.
 *
 * Consolidates shared logic between Product components:
 * - Quantity and variation state management
 * - Add to cart handling with cart conflict detection
 * - Analytics tracking (view_item event)
 * - Product data extraction from entity
 *
 * Uses useAddToCart internally for cart operations.
 *
 * @param entity - ImmutableJS entity (paragraph or product)
 * @param config - Configuration options
 *
 * @example
 * ```tsx
 * // For paragraph products
 * const { handleOrderClick, isLoading, variationPrice } = useProduct(
 *   entity,
 *   { entityPath: ['field_product', 0, 'entity'] }
 * );
 *
 * // For generic products (variations at root)
 * const { handleOrderClick, isLoading } = useProduct(
 *   entity,
 *   { entityPath: [] }
 * );
 * ```
 */
function useProduct(
  entity: Map<string, unknown>,
  config: ProductEntityConfig = {},
): UseProductReturn {
  const {
    entityPath = ['field_product', 0, 'entity'],
    minQuantity = 1,
    maxQuantity = 500,
    trackViewItem = true,
  } = config;

  const { addToCart, isLoading } = useAddToCart();
  const [quantity, setQuantity] = useState(minQuantity);
  const [variationIndex, setVariationIndex] = useState(0);

  // Track if page view has been sent for analytics
  const pageViewSent = useSelector((state: Map<string, unknown>) => state.getIn(['uiInternal', 'pageViewSent'], false));

  // Get product entity based on path
  const productEntity =
    entityPath.length > 0
      ? (entity.getIn(entityPath) as Map<string, unknown> | undefined)
      : entity;

  // Extract product data - variations is a List of Maps
  const variations = productEntity?.getIn(['variations']) as
    | List<Map<string, unknown>>
    | undefined;
  const currentVariation = variations?.get(variationIndex);

  const title = (productEntity?.getIn(['title', 0, 'value'], '') ||
    entity.getIn(['title', 0, 'value'], '')) as string;
  const variationPrice = (currentVariation?.getIn(['price', 'number']) ||
    0) as number;
  const sku = (currentVariation?.get('sku') || '') as string;
  const body = (productEntity?.getIn(['body', 0, 'value']) ||
    productEntity?.getIn(['body', 0, 'processed']) ||
    '') as string;
  const isAvailable = productEntity?.getIn(
    ['status', 0, 'value'],
    false,
  ) as boolean;
  const hasMultipleVariations = (variations?.size || 0) > 1;
  const isDigital = isDigitalProduct(productEntity as Map<string, unknown>);

  // Volume discount tiers are a computed field on the variation, exposed by the
  // backend product normalizer. Falls back to an empty list when none apply.
  // ImmutableJS types toJS() loosely, so the cast through unknown is required;
  // the backend guarantees the DiscountTier shape.
  const discountTiersRaw = currentVariation?.getIn([
    'volume_discount_tiers',
  ]) as List<Map<string, unknown>> | undefined;
  const discountTiers = (discountTiersRaw
    ? discountTiersRaw.toJS()
    : []) as unknown as DiscountTier[];

  // Resolve a plain image URL here so presentational components receive plain
  // data rather than an immutable media structure. Prefer the square (1_1)
  // image style for the product tile, falling back to any style or the raw url.
  const productImageField = productEntity?.getIn(['field_image', 0]) as
    | Map<string, unknown>
    | undefined;
  const imageStyles = productImageField?.get('styles') as
    | Map<string, string>
    | undefined;
  const productImage =
    imageStyles?.get('1_1') ??
    imageStyles?.first?.() ??
    (productImageField?.get('url') as string | undefined) ??
    null;

  // Track view_item analytics on mount
  useEffect(() => {
    if (!trackViewItem) return;
    if (typeof window === 'undefined' || !pageViewSent) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer = (window as any).dataLayer || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: 'EUR',
        value: variationPrice,
        items: [
          {
            item_id: sku,
            item_name: title,
            price: variationPrice,
            quantity,
          },
        ],
      },
    });
  }, [pageViewSent, quantity, sku, title, variationPrice, trackViewItem]);

  /**
   * Handles add to cart button click.
   * Uses the useAddToCart hook which handles cart conflicts,
   * analytics, and redirects automatically.
   */
  const handleOrderClick = useCallback(async (): Promise<void> => {
    const productUuid = currentVariation?.get('target_uuid') as string;

    if (!productUuid) {
      return;
    }

    await addToCart(
      {
        uuid: productUuid,
        sku,
        isDigitalProduct: isDigital,
        title,
        price: variationPrice,
      },
      quantity,
    );
  }, [
    addToCart,
    currentVariation,
    sku,
    isDigital,
    title,
    variationPrice,
    quantity,
  ]);

  /**
   * Handles variation selection change.
   * Defaults to index 0 if null/undefined is passed.
   */
  const handleVariationChange = useCallback((index: number | null): void => {
    setVariationIndex(index ?? 0);
  }, []);

  /**
   * Quantity setter with bounds checking
   */
  const handleSetQuantity = useCallback(
    (value: number): void => {
      const boundedValue = Math.max(minQuantity, Math.min(maxQuantity, value));
      setQuantity(boundedValue);
    },
    [minQuantity, maxQuantity],
  );

  return {
    quantity,
    setQuantity: handleSetQuantity,
    variationIndex,
    setVariationIndex,
    handleOrderClick,
    handleVariationChange,
    isLoading,
    title,
    variationPrice,
    sku,
    hasMultipleVariations,
    body,
    isDigitalProduct: isDigital,
    isAvailable,
    discountTiers,
    productImage,
  };
}

export default useProduct;
